import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TTicketingConfigurationNotification } from '../types/notification.type';

export type TGetTicketingConfigurationNotificationResponse = ApiResponse<TTicketingConfigurationNotification>;

export type TGetTicketingConfigurationNotificationParams = {
  spaceId: string;
};

export const getTicketingConfigurationNotification = async (
  params: TGetTicketingConfigurationNotificationParams
): Promise<TGetTicketingConfigurationNotificationResponse> => {
  const url = `/notifications/ticketing-configuration/${params?.spaceId}`;

  return axiosInstance.get(url);
};

export const GET_TICKETING_CONFIGURATION_NOTIFICATION_QUERY_KEY = 'get-ticketing-configuration-notification';

export const getTicketingConfigurationNotificationQueryOptions = <
  TData = TGetTicketingConfigurationNotificationResponse,
>(
  params: TGetTicketingConfigurationNotificationParams,
  select?: (data: TGetTicketingConfigurationNotificationResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_TICKETING_CONFIGURATION_NOTIFICATION_QUERY_KEY,
      {
        spaceId: params?.spaceId,
      },
    ],
    queryFn: () => getTicketingConfigurationNotification(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useTicketingConfigurationNotificationQuery<TData>(options: {
  params: TGetTicketingConfigurationNotificationParams;
  queryConfig?: QueryConfig<typeof getTicketingConfigurationNotificationQueryOptions<TData>>;
  select: (data: TGetTicketingConfigurationNotificationResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useTicketingConfigurationNotificationQuery(options: {
  params: TGetTicketingConfigurationNotificationParams;
  queryConfig?: QueryConfig<
    typeof getTicketingConfigurationNotificationQueryOptions<TGetTicketingConfigurationNotificationResponse>
  >;
}): ReturnType<typeof useQuery<TGetTicketingConfigurationNotificationResponse>>;

// Implementation
export function useTicketingConfigurationNotificationQuery<TData = TGetTicketingConfigurationNotificationResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetTicketingConfigurationNotificationParams;
  queryConfig?: QueryConfig<
    typeof getTicketingConfigurationNotificationQueryOptions<TData | TGetTicketingConfigurationNotificationResponse>
  >;
  select?: (data: TGetTicketingConfigurationNotificationResponse) => TData;
}) {
  return useQuery({
    ...getTicketingConfigurationNotificationQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<
      TData extends TGetTicketingConfigurationNotificationResponse
        ? TGetTicketingConfigurationNotificationResponse
        : TData
    >
  >;
}
